import {
  Pipe,
  PipeTransform,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { DomSanitizer, SafeStyle, SafeUrl } from '@angular/platform-browser';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs';
import { AuthorisedBlobService } from '../services/authorised-blob.service';
import { RequestStoreService, StoredRequest } from '../services/request-store.service';
import {
  fontAwesomeImageObjectUrl,
  fontAwesomeUserObjectUrl,
  SecureImagePipeFallbackImage
} from "@common/pipes/secure-image-pipe-fallback-images";

// Using similarity from AsyncPipe to avoid having to pipe |secure|async in HTML.
@Pipe({
  name: 'secureImage',
  pure: false
})
export class SecureImagePipe implements PipeTransform, OnDestroy {
  private _latestValue: any = null;
  private _latestReturnedValue: any = null;
  private _subscription: Subscription = null;
  private _obj: Observable<any> = null;

  private _result: BehaviorSubject<any> = new BehaviorSubject('');
  private result: Observable<any> = this._result.asObservable();
  private _internalSubscription: Subscription = null;

  constructor(
    private readonly _ref: ChangeDetectorRef,
    private readonly authorisedBlobService: AuthorisedBlobService,
    private readonly sanitizer: DomSanitizer,
    private readonly requestStoreService: RequestStoreService
  ) { }

  ngOnDestroy(): void {
    this._dispose();
  }

  transform(url: string,
            isBackground: boolean = false,
            fallbackImage: SecureImagePipeFallbackImage = SecureImagePipeFallbackImage.Image): any {
    const obj = this.internalTransform(url, isBackground, fallbackImage);
    return this.asyncTransform(obj);
  }

  private internalTransform(url: string,
                            isBackground: boolean,
                            fallbackImage: SecureImagePipeFallbackImage): Observable<any> {
    if (!url) {
      return this.result;
    }
    const previousRequest: StoredRequest = this.requestStoreService.getRequestByKey(url);
    let currentRequest: Observable<any> = null;

    if (!previousRequest) {
      const request = this.authorisedBlobService.get(url);
      const storedRequest = new StoredRequest();
      storedRequest.key = url;
      storedRequest.request = request.pipe(shareReplay(1));
      this.requestStoreService.storeRequest(storedRequest);
      currentRequest = storedRequest.request;
    }
    else {
      currentRequest = previousRequest.request;
    }

    this._internalSubscription = currentRequest.subscribe(m => {

      // the boolean literal 'false' here indicates an error occurred
      // in the AuthorizedBlobService when retrieving the requested image file
      if (m === false) {
        switch (fallbackImage) {
          case SecureImagePipeFallbackImage.Image:
            m = fontAwesomeImageObjectUrl;
            break;
          case SecureImagePipeFallbackImage.User:
            m = fontAwesomeUserObjectUrl;
            break;
        }
      }

      if (isBackground) {
        this._result.next(this.createSanitizedBackground(m));
      } else {
        this._result.next(this.createSanitizedUrl(m));
      }

    });
    return this.result;
  }

  createSanitizedBackground(url): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle('url(' + url + ')');
  }

  createSanitizedUrl(url): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  private asyncTransform(obj: Observable<any>): any {
    if (!this._obj) {
      if (obj) {
        this._subscribe(obj);
      }
      this._latestReturnedValue = this._latestValue;
      return this._latestValue;
    }
    if (obj !== this._obj) {
      this._dispose();
      return this.asyncTransform(obj);
    }
    if (this._latestValue === this._latestReturnedValue) {
      return this._latestReturnedValue;
    }
    this._latestReturnedValue = this._latestValue;
    return this._latestValue;
  }

  private _subscribe(obj: Observable<any>) {
    const _this = this;
    this._obj = obj;

    this._subscription = obj.subscribe({
      next(value) {
        return _this._updateLatestValue(obj, value);
      }, error: (e: any) => { throw e; }
    });
  }

  private _dispose() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }

    if (this._internalSubscription) {
      this._internalSubscription.unsubscribe();
      this._internalSubscription = null;
    }

    this._latestValue = null;
    this._latestReturnedValue = null;

    this._obj = null;
  }

  private _updateLatestValue(async: any, value: Object) {
    if (async === this._obj) {
      this._latestValue = value;
      this._ref.markForCheck();
    }
  }
}


